import { Box, useMediaQuery } from '@mui/material';
import { FiltersToolbar } from '@diana/core';
import { useTheme } from '@mui/system';
import { useFiltersStore } from '../../stores/useFilters';
import { useFilters, createPropertyToFilter } from '../../hooks/useFilters';
import RightDrawer from '../RightDrawer/RightDrawer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ResetButton } from '../Buttons/ResetButton';
import { ExportButton } from '../Buttons/ExportButton';
import { AlarmsDataGrid } from '../DataGrids/AlarmsDataGrid';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { useTranslation } from 'react-i18next';
import { HubsNames, useSignalRHubStore } from '../../stores/useSignalR';
import { useParams } from 'react-router-dom';
import { useMessageStore } from '../../stores/useMessageStore';
import { ConfirmationModal } from '../Modals/ConfirmationModal';
import { ErrorModal } from '../Modals/ErrorModal';
import { getAlarmsExportFileName } from '../../utils/stringUtils';
import { useExportHandler } from '../../hooks/useExportHandler';
import { ALARM_LOGS_NS } from './i18n';
import { getDefaultTimeRange } from '../../utils/timeRangeUtils';
import { convertFiltersToQuery } from '../../utils/convertFiltersToQuery';

const ALARM_LOGS_STORE_ID = 'alarm-logs';

export default function AlarmLogs() {
  const params = useParams();
  const [selectedBMT, setSelectedBMT] = useState(0);
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);
  const alarmsDataGridRef = useRef<GridApiPremium>(null);
  const [selectedRow, setSelectedRow] = useState({});
  const { t } = useTranslation([ALARM_LOGS_NS]);

  const {
    filtersValues,
    availableFilters,
    filters,
    setAvailableFilters,
    setFilters,
    setFiltersValues,
    resetData,
  } = useFiltersStore(ALARM_LOGS_STORE_ID, {
    timeRange: getDefaultTimeRange(),
    bmtNumber: [],
    barges: [],
    mfmDevice: [],
  });

  const { messages = [], loading } = useMessageStore((state) => Object.assign({}, {
    messages: state.messages[HubsNames.alarmsSummary],
    loading: state.stableLoadingFlags[HubsNames.alarmsSummary],
  }));

  const { defaultData, onFilterChange } = useFilters({
    createPropertyToFilter,
    filtersValues,
    setFiltersValues,
    availableFilters,
  });

  const {
    showExportModal,
    showErrorModal,
    isExporting,
    setShowExportModal,
    setShowErrorModal,
    handleExportClick,
    handleExportConfirm,
    handleExportRetry,
  } = useExportHandler({
    onExport: async (fileName: string) => {
      alarmsDataGridRef.current?.exportDataAsCsv({ fileName });
    },
    getFileName: getAlarmsExportFileName,
  });

  const setStreamArgs = useSignalRHubStore((state) => state.setStreamArgs);
  const disposeStream = useSignalRHubStore((state) => state.disposeStream);

  useEffect(() => {
    const query = convertFiltersToQuery(filtersValues);
    if (query) {
      setStreamArgs(HubsNames.alarmsSummary, query);
    }

    return () => {
      disposeStream(HubsNames.alarmsSummary);
    };
  }, [filtersValues, setStreamArgs, disposeStream]);

  useEffect(() => {
    if (params.deviceIdBmt) {
      const deviceIdBmt = +params.deviceIdBmt;
      if (!loading && messages.length > 0) {
        const matchingSummary = messages.reduce((found, row) => {
          if (found) return found;
          if (row.deliverySummaries && Array.isArray(row.deliverySummaries)) {
            return row.deliverySummaries.find((summary: { bunkerMeterTicketNumber: number }) => 
              summary.bunkerMeterTicketNumber === deviceIdBmt
            );
          }
          return null;
        }, null);
        
        if (matchingSummary) {
          setSelectedBMT(deviceIdBmt);
          setSelectedRow({
            bunkerMeterTicketNumber: deviceIdBmt,
            ...matchingSummary
          });
          setIsDrawerOpened(true);
        }
      }
    } else {
      // Reset drawer state when no BMT in URL
      setSelectedBMT(0);
      setSelectedRow({});
      setIsDrawerOpened(false);
    }
  }, [params.deviceIdBmt, loading, messages]);

  useEffect(() => {
    if (!isDrawerOpened) {
      setSelectedBMT(0);
    }
  }, [isDrawerOpened]);

  const handleBMTClick = useCallback((bmtItem: any, row: any, summary: any | null) => {
    setIsDrawerOpened(true);
    setSelectedBMT(bmtItem);
    setSelectedRow({
      bunkerMeterTicketNumber: bmtItem,
      ...summary,
    });
  }, []);

  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.up(700));

  const onResetAllClick = () => {
    resetData();
  };

  return (
    <Box sx={{ flexGrow: 1, mt: '10px', mb: '5px' }}>
      <Box
        component="section"
        display="flex"
        justifyContent="center"
        sx={{
          '.MuiStack-root': {
            flex: 1,
            minWidth: 0,
          },
        }}
      >
        <FiltersToolbar
          availableFilters={availableFilters.map((filter) =>
            createPropertyToFilter(filter.property, setFiltersValues),
          )}
          defaultData={defaultData}
          filters={filters}
          filtersValues={filtersValues}
          onFilterChange={onFilterChange}
          setFilters={setFilters}
          setAvailableFilters={setAvailableFilters}
          setFiltersValues={setFiltersValues}
          showAddButton={false}
          showResetButton={false}
          showRemoveButton={false}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-evenly',
            marginTop: '0',
            height: isMobile ? '36px' : 'auto',
            paddingBottom: isMobile ? 0 : '10px',
          }}
        >
          <ResetButton isDisabled={true} onResetAllClick={onResetAllClick} />
          <ExportButton onHandleExportData={handleExportClick} disabled={messages.length === 0} />
        </Box>
      </Box>
      <AlarmsDataGrid
        ref={alarmsDataGridRef}
        data={messages}
        loading={loading}
        handleBMTClick={handleBMTClick}
      />
      <RightDrawer
        data={selectedRow}
        selectedBMT={selectedBMT}
        isDrawerOpened={isDrawerOpened}
        setIsDrawerOpened={setIsDrawerOpened}
      />
      <ConfirmationModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
        title={t('components/AlarmLogs:modals.export.title')}
        message={t('components/AlarmLogs:modals.export.message')}
        confirmText={t('components/AlarmLogs:modals.export.confirmText')}
        cancelText={t('components/AlarmLogs:modals.export.cancelText')}
        isLoading={isExporting}
      />

      <ErrorModal
        open={showErrorModal}
        onClose={() => setShowErrorModal(true)}
        onRetry={handleExportRetry}
        message={t('components/AlarmLogs:modals.export.error.message')}
      />
    </Box>
  );
}
